import { Inject, Injectable } from '@angular/core';
import { logError } from '@office-start/telemetry-service';

import { PwaConfig } from '../../pwa/services/pwa-config.service';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { AccessTokenConsumer, AccessTokenService } from '../../shared/services/access-token.service';
import { ConfigParserService } from '../../shared/services/config-parser.service';
import { OfficeHomeApiConfigService } from '../../shared/services/officehome-api-config.service';
import { WindowService } from '../../shared/services/window.service';
import { AuthType, ClientStartupConfigService } from '../../shared/shared-context/client-startup-config.service';
import { ContextService } from '../../shared/shared-context/context.service';
import { SERVICE_WORKER_ENABLED } from '../service-worker-enabled.injection-token';
import { SERVICE_WORKER_USER_ID_FACTORY } from '../service-worker-user-id.injection-token';
import { AuthorizedWarmupMessage, AuthorizedWarmupMessagePayloadV2, ServiceWorkerAuthorizedInstallService } from './service-worker-authorized-install.service';

/**
 * Responsible for providing the Office.com Access Token to the installing worker for PWA so that Service Worker can complete installation
 */
@Injectable()
export class PwaServiceWorkerAuthorizedInstallService extends ServiceWorkerAuthorizedInstallService {

    constructor (
        private readonly accessTokenService: AccessTokenService,
        private readonly configParserService: ConfigParserService,
        private readonly contextService: ContextService,
        private readonly instrumentationService: InstrumentationService,
        private readonly officeApiService: OfficeHomeApiConfigService,
        @Inject(SERVICE_WORKER_ENABLED) private readonly serviceWorkerEnabled: boolean,
        @Inject(SERVICE_WORKER_USER_ID_FACTORY) private readonly serviceWorkerUserIdFactory: () => Promise<string>,
        private readonly startupConfigService: ClientStartupConfigService,
        windowService: WindowService
    ) {
        super(windowService);
    }

    public async handleRegistration(registration: ServiceWorkerRegistration): Promise<void> {
        if (!this.serviceWorkerEnabled) {
            return;
        }

        try {
            const serviceWorkerIntervalUpdateEnabled = this.contextService.sharedContext.serviceWorkerIntervalUpdateEnabled;
            const postInstallMessageOnUpdate = () => {
                registration.addEventListener('updatefound', () => {
                    this.postAuthorizedInstallMessage(registration.installing);
                });
            };

            if (registration.installing) {
                this.postAuthorizedInstallMessage(registration.installing);
                if (serviceWorkerIntervalUpdateEnabled) {
                    postInstallMessageOnUpdate();
                }
            } else {
                postInstallMessageOnUpdate();
            }
        } catch (ex) {
            const params: InstrumentationParams = {
                [InstrumentationParam.Error]: (ex || '').toString()
            };
            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PwaSwHandleRegistrationError, params);
            logError({ eventName: 'Pwa_Sw_Handle_Registration', message : (ex || '').toString() });
        }
    }

    private async postAuthorizedInstallMessage(installingWorker: ServiceWorker) {
        const isConsumer = this.startupConfigService.config.authType === AuthType.MSA;

        const payload: AuthorizedWarmupMessagePayloadV2 = {
            token: null,
            userId: await this.serviceWorkerUserIdFactory()
        };

        if (!isConsumer || (this.startupConfigService.config.sharedClientStartupContext.pwaV2TokenEnabled && this.officeApiService.config.jwtAuthEnabled)) {
            const officeHomeAccessToken = await this.accessTokenService.getAccessToken(AccessTokenConsumer.OfficeHome);

            if (!officeHomeAccessToken.success) {
                const params: InstrumentationParams = {
                    [InstrumentationParam.Error]: (officeHomeAccessToken.error || '').toString()
                };
                this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.PwaSwGetTokenError, params);
                logError({ eventName: 'Pwa_Sw_Get_Token_Error', message: (officeHomeAccessToken.error || '').toString() });
                return;
            }

            payload.token = officeHomeAccessToken.token;
        } else {
            // Sending random character as token for MSA users since we don't check it. Avoid making changes on the SW side because
            // if we need to do so in the future, we don't have to update it again
            payload.token = '.';
        }

        payload.apiQsParams = {
            [window.forcedMockAuth ? 'mockauth' : 'auth']: isConsumer ? '1' : '2'
        };

        if (this.contextService.sharedContext.inMockMode) {
            this.addParamFromWindow(payload, 'envType');
        }

        this.addParamFromWindow(payload, 'flight');

        const pwaConfig = this.configParserService.parse<PwaConfig>('pwaConfig');

        if (pwaConfig && pwaConfig.shellAndConfigApiEndpoint) {
            payload.apiPath = pwaConfig.shellAndConfigApiEndpoint;
        }

        const message: AuthorizedWarmupMessage = {
            type: 'OFFICECOM_AUTH_WARMUP',
            meta: 'installation-credentials',
            payload
        };

        installingWorker.postMessage(message);
    }
}
